import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { api, fmtDateTime } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

export default function NotificationBell() {
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);

  const refreshCount = async () => {
    try { const r = await api.get("/notifications/unread-count"); setUnread(r.data.unread || 0); } catch {}
  };

  const refreshList = async () => {
    try { const r = await api.get("/notifications"); setItems(r.data); } catch {}
  };

  useEffect(() => {
    refreshCount();
    refreshList();
    timerRef.current = setInterval(refreshCount, 30000);
    return () => clearInterval(timerRef.current);
  }, []);

  useEffect(() => { if (open) refreshList(); }, [open]);

  const markAll = async () => {
    await api.post("/notifications/mark-all-read");
    refreshCount(); refreshList();
  };

  const clickItem = async (n) => {
    if (!n.read_at) await api.post(`/notifications/${n.id}/read`);
    setOpen(false);
    if (n.link) nav(n.link);
    refreshCount();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-md hover:bg-slate-100" data-testid="notification-bell">
          <Bell className="w-5 h-5 text-slate-700" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-rose-600 text-white text-[10px] rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center font-semibold" data-testid="notification-badge">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end" data-testid="notification-panel">
        <div className="p-3 border-b flex items-center justify-between">
          <div className="font-semibold text-sm">Benachrichtigungen</div>
          <Button variant="ghost" size="sm" onClick={markAll} data-testid="notif-mark-all-read"><CheckCheck className="w-4 h-4 mr-1" /> Alle gelesen</Button>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {items.length === 0 && <div className="p-6 text-center text-sm text-slate-500">Keine Benachrichtigungen.</div>}
          {items.map((n) => (
            <button key={n.id} onClick={() => clickItem(n)} data-testid={`notif-item-${n.id}`}
              className={`block w-full text-left p-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 ${!n.read_at ? "bg-blue-50/50" : ""}`}>
              <div className="text-sm font-medium text-slate-900 truncate">{n.title}</div>
              {n.body && <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{n.body}</div>}
              <div className="text-[11px] text-slate-400 mt-1 font-mono">{fmtDateTime(n.created_at)}</div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
